// Asset portfolio tree (spec S1).
//
// Each row is a Link to /$org/assets/$assetUid (the detail page). The chevron
// is a sibling button outside the Link and stops propagation, so users can
// expand/collapse without navigating. On hover (desktop only), a `+` button
// appears for adding a child asset.
//
// Per-type columns come from `getAssetRowColumns` — today the address and the
// External ID. The column resolver carries TODO markers for the rest.

import { useState, useEffect } from 'react'
import { Link } from '@tanstack/react-router'
import { AssetRelationsBadge } from './asset-relations/AssetRelationsBadge'
import { useAssets } from '../api/queries'
import type { Asset } from '../hooks/useQueries'
import { useIsMobile } from '../hooks/use-mobile'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible'
import {
    mdiChevronRight,
    mdiChevronDown,
    mdiLoading,
    mdiPlus,
    mdiWrench,
} from '@mdi/js'
import { AppIcon } from './ui/icon'
import { AssetName } from './ArchivedMarker'
import { AssetTypeTile } from './assets/AssetTypeTile'
import { getAssetRowColumns, type AssetRowColumn } from './asset-tree-columns'

// Only the children of these types can be address-bearing (sites and parcels
// hold buildings and houses). Deeper levels — floors, units, equipment — would
// pay for a full properties payload they never display.
const CHILDREN_MAY_HAVE_ADDRESS = new Set(['building_group', 'parcel_land'])

/** Width of the name cell at depth 0. Deeper rows give back exactly what
 *  their indentation takes, so the columns after the name stay put. */
const NAME_COL_PX = 280

/** Per-level indentation: the row's own padding, plus the 8px margin and 1px
 *  left border of the wrapper each level nests inside. */
const indentPx = (level: number, isMobile?: boolean) => level * ((isMobile ? 12 : 20) + 9)

type TreeViewAsset = Asset & {
    parent_chain?: boolean
    children?: TreeViewAsset[]
}

interface AssetType {
    uid: string
    code: string
    name: string | Record<string, string>
    emoji?: string
    icon?: string
    kind?: string
    properties?: Record<string, unknown>
}

interface TreeViewProps {
    orgCode: string
    rootAssets: TreeViewAsset[]
    assetTypes: AssetType[]
    selectedAssetUid?: string
    expandedNodeIds?: string[]
    isSearchResult?: boolean
    editionMode?: boolean
    /** S6: narrow mode for the Hiérarchie side panel — drops per-type
     *  columns, hover-`+`, and the new-tab `↗` icon. Just icon + name. */
    narrow?: boolean
    onAssetMove?: (assetUid: string, newParentUid: string | null) => void
}

/**
 * The asset currently being dragged, for the duration of one drag.
 *
 * Module-scoped rather than React state on purpose: a drag is a single global
 * gesture, and `dataTransfer.getData` answers an empty string on dragenter /
 * dragover in every browser's protected mode — so the rows cannot read what is
 * being dragged at the moment they have to decide whether to light up as a
 * target. Cleared on drop and on dragend.
 */
let draggedAssetUid: string | null = null

interface TreeNodeProps {
    asset: TreeViewAsset
    orgCode: string
    assetTypes: AssetType[]
    level: number
    selectedAssetUid?: string
    expandedNodeIds?: string[]
    isSearchResult?: boolean
    editionMode?: boolean
    narrow?: boolean
    isMobile?: boolean
    /**
     * This row's ancestors, root-first, NOT including the row itself. It is
     * what makes the client-side cycle refusal exact: a drop is refused when
     * the dragged asset is this row or one of these (spec 2026-09-18-01). The
     * rail loads children lazily, so there is no flat tree to consult —
     * the recursion carries the chain instead, and it costs nothing.
     */
    ancestorUids?: string[]
    onAssetMove?: (assetUid: string, newParentUid: string | null) => void
}

function TreeNode({ asset, orgCode, assetTypes, level, selectedAssetUid, expandedNodeIds, isSearchResult, editionMode, narrow, isMobile, ancestorUids = [], onAssetMove }: TreeNodeProps) {
    const shouldBeExpanded = expandedNodeIds?.includes(asset.uid) || (isSearchResult && asset.parent_chain) || false
    const [isOpen, setIsOpen] = useState(shouldBeExpanded)
    const [userExpanded, setUserExpanded] = useState(false)
    const [isDraggedOver, setIsDraggedOver] = useState(false)

    useEffect(() => {
        const newShouldBeExpanded = expandedNodeIds?.includes(asset.uid) || (isSearchResult && asset.parent_chain) || false
        if (newShouldBeExpanded || userExpanded) {
            setIsOpen(true)
        } else {
            setIsOpen(false)
            setUserExpanded(false)
        }
    }, [expandedNodeIds, asset.uid, userExpanded, isSearchResult, asset.parent_chain])

    const assetType = assetTypes.find(t => t.uid === asset.asset_type_uid)
    const assetTypeCode = assetType?.code

    // S1 fix: expandable for every type, including Floor / Unit / Equipment.
    const hasChildren = Boolean(
        (asset.children && asset.children.length > 0) ||
        (asset.children_count && asset.children_count > 0)
    )

    const shouldFetchChildren = isOpen && hasChildren && !isSearchResult && !asset.children

    const {
        data: fetchedChildren = [],
        isLoading: childrenLoading,
        error: childrenError,
    } = useAssets(
        orgCode,
        undefined,
        undefined,
        shouldFetchChildren ? asset.uid : undefined,
        'type',
        assetTypeCode && CHILDREN_MAY_HAVE_ADDRESS.has(assetTypeCode)
            ? ['children_count', 'flags', 'external_ids', 'properties', 'link_counts']
            : ['children_count', 'flags', 'external_ids', 'link_counts'],
        undefined,
        undefined,
        undefined,
        // Browse-by-parent tree: equipment doesn't belong among tree children
        // (spec 2026-07-24-02-asset-type-in-tree-flag).
        true,
    )

    const children = asset.children || fetchedChildren
    const paddingLeft = level * (isMobile ? 12 : 20)

    // S1: per-type columns (address + External ID for now; rest TODO).
    const columns: AssetRowColumn[] = getAssetRowColumns(asset, assetTypeCode)

    // Columns only exist on the wide desktop tree.
    const showColumns = !narrow && !isMobile

    // The name cell is fixed-width and shrinks by exactly the row's
    // indentation, so the columns after it start at the same x on every row
    // whatever its depth — that's what makes them read as columns.
    const nameWidthPx = Math.max(140, NAME_COL_PX - indentPx(level, isMobile))
    const nameClassName = `truncate ${showColumns ? 'shrink-0' : 'flex-1'}`
    const nameStyle = showColumns ? { width: `${nameWidthPx}px` } : undefined

    // Drag and drop handlers (preserved from the previous version)
    const handleDragStart = (e: React.DragEvent) => {
        if (!editionMode) return
        e.dataTransfer.setData('text/plain', asset.uid)
        e.dataTransfer.effectAllowed = 'move'
        draggedAssetUid = asset.uid
    }
    const handleDragEnd = () => { draggedAssetUid = null }
    const handleDragOver = (e: React.DragEvent) => {
        if (!editionMode) return
        e.preventDefault()
        e.stopPropagation()
        e.dataTransfer.dropEffect = 'move'
    }
    // An asset may not be filed under itself or under anything below it: the
    // backend answers 400, and a cycle is what stops the organization's whole
    // asset_hierarchy job from terminating. Refused here so the row never
    // lights up as a valid target in the first place.
    const refusesDrop = (uid: string | null) =>
        !uid || uid === asset.uid || ancestorUids.includes(uid)

    const handleDragEnter = (e: React.DragEvent) => {
        if (!editionMode) return
        e.preventDefault()
        e.stopPropagation()
        // `dataTransfer.getData` is empty until the drop in every browser's
        // protected drag mode, so the highlight reads the module-scoped uid
        // the drag start recorded — the drop below still re-checks.
        if (!refusesDrop(draggedAssetUid)) {
            setIsDraggedOver(true)
        }
    }
    const handleDragLeave = (e: React.DragEvent) => {
        if (!editionMode) return
        e.preventDefault()
        e.stopPropagation()
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            setIsDraggedOver(false)
        }
    }
    const handleDrop = (e: React.DragEvent) => {
        if (!editionMode) return
        e.preventDefault()
        e.stopPropagation()
        const dropped = e.dataTransfer.getData('text/plain') || draggedAssetUid
        setIsDraggedOver(false)
        draggedAssetUid = null
        if (refusesDrop(dropped)) return
        onAssetMove?.(dropped as string, asset.uid)
    }

    return (
        <div className="border-l border-border/30 ml-2">
            <Collapsible
                open={isOpen}
                onOpenChange={(open) => {
                    setIsOpen(open)
                    setUserExpanded(open)
                }}
            >
                <div
                    className={`group flex items-center gap-1.5 md:gap-2 py-1.5 md:py-2 pr-2 md:pr-4 rounded-r-md transition-all duration-200 ${
                        editionMode ? 'cursor-move' : ''
                    } ${
                        isDraggedOver && editionMode
                            ? 'bg-primary/20 border-2 border-primary border-dashed shadow-lg'
                            : 'hover:bg-muted/50'
                    } ${
                        selectedAssetUid === asset.uid ? 'bg-muted/30' : ''
                    }`}
                    style={{ paddingLeft: `${paddingLeft + (isMobile ? 4 : 8)}px` }}
                    data-testid={`tree-row-${asset.uid}`}
                    data-drop-active={isDraggedOver && editionMode ? 'true' : undefined}
                    draggable={editionMode}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    onDragOver={handleDragOver}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                >
                    {/* Chevron — sibling of the row Link, stops propagation. */}
                    {hasChildren ? (
                        <CollapsibleTrigger asChild>
                            <button
                                className="flex items-center justify-center w-6 h-6 hover:bg-muted rounded-sm shrink-0"
                                data-testid={`tree-toggle-${asset.uid}`}
                                onClick={(e) => e.stopPropagation()}
                                aria-label={isOpen ? 'Collapse' : 'Expand'}
                            >
                                {childrenLoading ? (
                                    <AppIcon path={mdiLoading} size="xs" className="motion-safe:animate-spin" />
                                ) : (
                                    <AppIcon tone="muted" path={isOpen ? mdiChevronDown : mdiChevronRight} size="xs" />
                                )}
                            </button>
                        </CollapsibleTrigger>
                    ) : (
                        <div className="w-6 h-6 shrink-0" />
                    )}

                    <AssetTypeTile
                        type={assetType}
                        asset={asset}
                        size="sm"
                        title={`${assetType?.code || 'unknown'} asset`}
                    />

                    {/* Row Link — the entire row navigates to the detail page,
                        except the chevron (above), the +/↗ buttons (below) and
                        edit-mode handles. */}
                    {editionMode ? (
                        <div
                            className={`${nameClassName} ${
                                asset.parent_chain === true
                                    ? 'text-muted-foreground'
                                    : 'text-foreground'
                            }`}
                            style={nameStyle}
                            data-testid={`tree-asset-${asset.uid}`}
                        >
                            <AssetName name={asset.name ?? ''} archivedAt={asset.archived_at} />
                            {hasChildren ? (
                                <span className="text-muted-foreground ml-1">({asset.children_count})</span>
                            ) : null}
                        </div>
                    ) : (
                        <Link
                            to="/$org/assets/$assetUid"
                            params={{ org: orgCode, assetUid: asset.uid }}
                            className={`${nameClassName} hover:underline ${
                                asset.parent_chain === true
                                    ? 'text-muted-foreground hover:text-muted-foreground/80'
                                    : 'text-foreground hover:text-primary'
                            }`}
                            style={nameStyle}
                            data-testid={`tree-asset-${asset.uid}`}
                        >
                            <AssetName name={asset.name ?? ''} archivedAt={asset.archived_at} />
                            {hasChildren ? (
                                <span className="text-muted-foreground ml-1">({asset.children_count})</span>
                            ) : null}
                        </Link>
                    )}

                    {/* Typed asset relations (spec 2026-08-16-03 §6): a link
                        icon + count so a desserte edge is visible while
                        browsing the tree, not only on the detail page. Only
                        renders when the caller asked for ?include=link_counts
                        and the asset actually has edges. */}
                    <AssetRelationsBadge count={(asset as any).link_counts?.relations} />

                    {/* Per-type columns. Dropped in narrow mode (S6) and on mobile. */}
                    {showColumns && columns.map((col) => (
                        <span
                            key={col.key}
                            className={`text-xs text-muted-foreground shrink-0 truncate text-left ${
                                col.mono ? 'font-mono' : ''
                            }`}
                            style={{
                                width: `${col.widthPx}px`,
                                marginLeft: col.gapCm ? `${col.gapCm}cm` : undefined,
                            }}
                            title={col.value ?? undefined}
                            data-testid={`tree-col-${col.key}-${asset.uid}`}
                        >
                            {col.value ?? ''}
                        </span>
                    ))}

                    {/* Pushes the trailing icons back to the right edge, now
                        that the name cell no longer stretches. */}
                    {showColumns ? <div className="flex-1" /> : null}

                    {/* Wrench for incomplete groups (preserved). */}
                    {assetTypeCode === 'building_group' && asset.flags?.includes('incomplete') && (
                        <Link
                            to="/$org/createBuildingGroup"
                            params={{ org: orgCode }}
                            search={{ asset_uid: asset.uid }}
                            className="hover:scale-110 transition-transform ml-1 shrink-0"
                            title="Configure incomplete building group"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <AppIcon tone="muted" path={mdiWrench} size="sm" />
                        </Link>
                    )}
                    {assetTypeCode === 'building' && asset.flags?.includes('incomplete') && (
                        <Link
                            to="/$org/createBuilding"
                            params={{ org: orgCode }}
                            search={{ asset_uid: asset.uid }}
                            className="hover:scale-110 transition-transform ml-1 shrink-0"
                            title="Configure incomplete building"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <AppIcon tone="muted" path={mdiWrench} size="sm" />
                        </Link>
                    )}

                    {/* Hover-only `+` (desktop only). Hidden in narrow mode (S6) and on mobile. */}
                    {!narrow && (
                        <Link
                            to="/$org/assets/createBasic"
                            params={{ org: orgCode }}
                            search={{ parent_asset_uid: asset.uid } as any}
                            className="hidden md:inline-flex opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-primary transition-opacity ml-1 shrink-0"
                            title="Add a child asset"
                            data-testid={`tree-add-child-${asset.uid}`}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <AppIcon path={mdiPlus} size="sm" />
                        </Link>
                    )}
                </div>

                {hasChildren ? (
                    <CollapsibleContent>
                        {childrenError ? (
                            <div className="text-destructive text-xs ml-8 py-2">
                                Failed to load children
                            </div>
                        ) : (
                            <div>
                                {(children as TreeViewAsset[]).map((child) => (
                                    <TreeNode
                                        key={child.uid}
                                        asset={child}
                                        orgCode={orgCode}
                                        assetTypes={assetTypes}
                                        level={level + 1}
                                        selectedAssetUid={selectedAssetUid}
                                        expandedNodeIds={expandedNodeIds}
                                        isSearchResult={isSearchResult}
                                        editionMode={editionMode}
                                        narrow={narrow}
                                        isMobile={isMobile}
                                        ancestorUids={[...ancestorUids, asset.uid]}
                                        onAssetMove={onAssetMove}
                                    />
                                ))}
                            </div>
                        )}
                    </CollapsibleContent>
                ) : null}
            </Collapsible>
        </div>
    )
}

export function TreeView({ orgCode, rootAssets, assetTypes, selectedAssetUid, expandedNodeIds, isSearchResult, editionMode, narrow, onAssetMove }: TreeViewProps) {
    const isMobile = useIsMobile()
    const handleAssetMove = (assetUid: string, newParentUid: string | null) => {
        onAssetMove?.(assetUid, newParentUid)
    }

    const handleRootDragOver = (e: React.DragEvent) => {
        if (!editionMode) return
        e.preventDefault()
        e.dataTransfer.dropEffect = 'move'
    }

    const handleRootDrop = (e: React.DragEvent) => {
        if (!editionMode) return
        e.preventDefault()
        const dropped = e.dataTransfer.getData('text/plain') || draggedAssetUid
        draggedAssetUid = null
        // Detaching to the root: no cycle is possible, the root is nobody's
        // descendant.
        if (dropped) handleAssetMove(dropped, null)
    }

    return (
        <div
            className="space-y-1"
            data-testid="tree-root-drop"
            onDragOver={handleRootDragOver}
            onDrop={handleRootDrop}
        >
            {rootAssets.map((asset) => (
                <TreeNode
                    key={asset.uid}
                    asset={asset}
                    orgCode={orgCode}
                    assetTypes={assetTypes}
                    level={0}
                    selectedAssetUid={selectedAssetUid}
                    expandedNodeIds={expandedNodeIds}
                    isSearchResult={isSearchResult}
                    editionMode={editionMode}
                    narrow={narrow}
                    isMobile={isMobile}
                    onAssetMove={handleAssetMove}
                />
            ))}
        </div>
    )
}
